import { isNil } from 'lodash';
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@shared/components/Button';
import { Menu } from '@shared/components/Menu';
import { useConfirm } from '@shared/components/Modal';
import { useToast } from '@shared/components/Toast';
import { View } from '@shared/components/View';
import { MBEmptyListPlaceholder } from '@shared/components/list';
import { LoadingIndicator } from '@shared/components/loading';
import { useDeleteEntitiesMutation } from '@shared/scenes/cards/entityBulkActions/useDeleteWorkflowEntities';
import { CardFormTypeContext } from '@shared/scenes/entity/cardFormTypeContext';
import { EntityForm } from '@shared/scenes/entity/components/EntityForm';
import { FormScrollView } from '@shared/scenes/entity/components/FormScrollView';
import { FormScrollProvider, FormSearchProvider } from '@shared/scenes/entity/context';
import { useCreateCardEntity } from '@shared/scenes/entity/create/hooks/useCreateCardEntity';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { useEditCardEntity } from '@shared/scenes/entity/edit/hooks/useEditCardEntity';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { HotKey, useHotkeyBinding, useShowHotkeyTooltips } from '@shared/util/hotkeys';
import { i18n } from '@shared/util/i18n';
import { useUnsavedChangesNavigationWarning } from '@shared/util/navigation';
import { SubformNavigationContext } from '../Subform/SubformEditField';
import {
  SubtableDraftsContext,
  SubtableFieldDrafts,
  useReportDirtyRowForm,
} from './subtableDrafts';
import { SubtableRouteContext } from './subtableRoute';
import { getSubtableRowLimitFromError, useSubtableRowLimitAlert } from './subtableRowLimit';
import { SubtableRow } from './useSubtableRowMenu';
import styles from './SubtableRowMenu.module.css';

const FIRST_FOCUSABLE_SELECTOR =
  ':is(input, textarea, button):not([disabled], [readonly], [type="hidden"])';

interface Props {
  workflowTemplate?: API.WorkflowTemplateFragment;
  /** The card whose rows these are; a new row is created under it. Unset for draft rows. */
  parentEntityId?: string;
  /**
   * The Create form's draft rows: a new row is added to them, and a row opened is one of them,
   * edited and deleted in place, all without the API.
   */
  drafts?: SubtableFieldDrafts;
  /** False on a public external form: the row form hides and limits fields as the card form does. */
  isAuthenticated?: boolean;
  isOpen: boolean;
  /** The row being edited; omit to add a new row. */
  row?: SubtableRow | null;
  /** Shows the row without Save, every column read-only (a read-only card). */
  isReadOnly?: boolean;
  /**
   * None of the subtable's fields would show as a column (see getSubtableColumns): a new row shows
   * why it can't be added instead of an empty form, which would save a blank row.
   */
  hasNoVisibleFields?: boolean;
  /**
   * The columns behind hasNoVisibleFields are still loading (a menu opened from the URL on page
   * load): a new row waits, rather than showing the form until they arrive.
   */
  isColumnsLoading?: boolean;
  /** Opens the subtable in the builder, from the no-visible-fields state; omit without config access. */
  onConfigure?: () => void;
  /** The modal whose right edge the menu docks to on desktop. */
  containerAnchorName?: string;
  /** After a row is added or saved, before the menu closes. */
  onSaved: () => void;
  onClose: () => void;
}

/**
 * The overlay for adding or editing a subtable row: a form built from the subtable's Create or
 * Update view template.
 * On desktop it takes the chat sidebar's footprint in the surrounding modal; on mobile it's a
 * bottom sheet. Menu.Content unmounts when closed, so every open starts clean.
 */
export function SubtableRowMenu({
  workflowTemplate,
  parentEntityId,
  drafts,
  isAuthenticated = true,
  isOpen,
  row,
  isReadOnly = false,
  hasNoVisibleFields = false,
  isColumnsLoading = false,
  onConfigure,
  containerAnchorName,
  onSaved,
  onClose,
}: Props) {
  // With lightDismiss off the browser no longer closes the popover on Escape, so close it here.
  // On the panel rather than the form, so it also catches Escape while the panel itself has focus
  // (the focus trap's default). Capture phase, because the form's react-aria inputs stop Escape
  // from bubbling. Only for keys whose target is in the panel's own DOM: a nested popup (an open
  // select's listbox) portals elsewhere and keeps its Escape. preventDefault tells the
  // surrounding modal the Escape was handled (shouldCloseOnEscape), so it stays open. A form with
  // unsaved changes (data-dirty, set by RowFormLayout) swallows Escape without closing, so a stray
  // key press can't discard the edits; Close or Save still work.
  const closeOnEscape = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !event.currentTarget.contains(event.target as Node)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (isNil(event.currentTarget.querySelector('[data-dirty]'))) {
      onClose();
    }
  };

  return (
    // Menu reports every change to its open state, including following isOpen. Only a close it
    // made itself, while isOpen still says open, is the user's: echoing one that came from isOpen
    // would close the row's route over whatever just replaced it (opening the expanded modal).
    <Menu
      isOpen={isOpen}
      onOpenChange={isNowOpen => !isNowOpen && isOpen && onClose()}
      closeOnSelect={false}
    >
      <Menu.Content
        lightDismiss={false}
        mobileSheet
        className={styles.subtableRowMenu}
        vars={{ 'subtable-row-menu-container': containerAnchorName }}
        onKeyDownCapture={closeOnEscape}
      >
        {!isNil(workflowTemplate) && !isNil(row) && !isNil(drafts) && (
          <DraftRowForm
            key={row.entityId}
            workflowTemplate={workflowTemplate}
            row={row}
            drafts={drafts}
            isAuthenticated={isAuthenticated}
            onSaved={onSaved}
            onClose={onClose}
          />
        )}
        {!isNil(workflowTemplate) && !isNil(row) && isNil(drafts) && (
          <EditRowForm
            // Another row starts a fresh form.
            key={row.entityId}
            workflowTemplate={workflowTemplate}
            row={row}
            isReadOnly={isReadOnly}
            isAuthenticated={isAuthenticated}
            onSaved={onSaved}
            onClose={onClose}
          />
        )}
        {!isNil(workflowTemplate) && isNil(row) && isColumnsLoading && (
          <ColumnsLoading onClose={onClose} />
        )}
        {!isNil(workflowTemplate) && isNil(row) && !isColumnsLoading && hasNoVisibleFields && (
          <NoVisibleFields onConfigure={onConfigure} onClose={onClose} />
        )}
        {!isNil(workflowTemplate) && isNil(row) && !isColumnsLoading && !hasNoVisibleFields && (
          <NewRowForm
            workflowTemplate={workflowTemplate}
            parentEntityId={parentEntityId}
            drafts={drafts}
            isAuthenticated={isAuthenticated}
            onSaved={onSaved}
            onClose={onClose}
          />
        )}
      </Menu.Content>
    </Menu>
  );
}

interface RowFormProps {
  workflowTemplate: API.WorkflowTemplateFragment;
  isAuthenticated: boolean;
  onSaved: () => void;
  onClose: () => void;
}

// Like subforms, a subtable only gets Create / Update view templates once someone customizes them.
// Without one, the form renders the template's components in order.

function NewRowForm({
  workflowTemplate,
  isAuthenticated,
  parentEntityId,
  drafts,
  onSaved,
  onClose,
}: RowFormProps & { parentEntityId?: string; drafts?: SubtableFieldDrafts }) {
  const viewTemplateId = workflowTemplate.viewTemplateDefaults?.create;
  const hasViewTemplate = !isNil(viewTemplateId);
  const showRowLimitAlert = useSubtableRowLimitAlert();
  const toast = useToast();
  // Someone else may have added the last row while this form was open: say so, then reload the
  // rows so the field's count and Add row catch up, and close the form. Any other failure toasts
  // and keeps the form open, so the user can try again.
  const [isAtRowLimit, setIsAtRowLimit] = useState(false);
  const onSaveError = useCallback(
    async (error: unknown) => {
      const rowLimit = getSubtableRowLimitFromError(error);
      if (!isNil(rowLimit)) {
        await showRowLimitAlert(rowLimit);
        setIsAtRowLimit(true);
      } else {
        toast({ style: 'warning', message: i18n('errors.somethingWentWrong') });
      }
    },
    [showRowLimitAlert, toast],
  );
  const {
    fields,
    updateField,
    components,
    errors,
    saveAttempted,
    viewTemplate,
    status,
    isDirty,
    onPressSave,
  } = useCreateCardEntity({
    workflowTemplateId: workflowTemplate.id,
    viewTemplateId,
    parentEntityId,
    onSaveError,
    onSaveDraft: drafts?.add,
  });
  // Leaving the form with unsaved edits asks first, the same as an existing row's. Not once it's
  // added, or after the row limit alert, which close it on purpose. Before useCloseOnDone, so its
  // close sees the warning already off.
  const isDone = status === 'success' || isAtRowLimit;
  // A draft's form tells the Create form instead, whose own warning covers it: a second one here
  // would ask again as the Create form leaves for the new card.
  useUnsavedChangesNavigationWarning(isDirty && !isDone && isNil(drafts));
  useReportDirtyRowForm(drafts, isDirty && !isDone);
  useCloseOnDone(isDone, onSaved, onClose);

  return (
    <RowFormLayout
      isAuthenticated={isAuthenticated}
      formType="CREATE"
      title={i18n('card.blocks.subtableInput.newRow')}
      workflowTemplateId={workflowTemplate.id}
      viewTemplateId={viewTemplate?.id}
      isLoading={isNil(fields) || (hasViewTemplate && isNil(viewTemplate))}
      isSaving={status === 'loading' && !isNil(fields)}
      saveLabel={i18n('common.add')}
      showSave
      isDirty={isDirty}
      isReadOnly={false}
      fields={fields}
      updateField={updateField}
      components={components}
      errors={errors}
      saveAttempted={saveAttempted}
      onSave={onPressSave}
      onClose={onClose}
    />
  );
}

/** Until it's known whether the new row gets a form or NoVisibleFields: only Close. */
function ColumnsLoading({ onClose }: { onClose: () => void }) {
  return (
    <View className={styles.form}>
      <Menu.TitleBar startContent={<Menu.CloseButton onClick={onClose} />} />
      <View className={styles.loading}>
        <LoadingIndicator />
      </View>
    </View>
  );
}

/** In place of the new row form when the subtable has no visible fields: no Add, only Close. */
function NoVisibleFields({
  onConfigure,
  onClose,
}: {
  onConfigure?: () => void;
  onClose: () => void;
}) {
  return (
    <View className={styles.form}>
      <Menu.TitleBar startContent={<Menu.CloseButton onClick={onClose} />} />
      <View className={styles.noVisibleFields}>
        <FormScrollView className={styles.body}>
          <MBEmptyListPlaceholder
            icon="table-grid"
            title={i18n('card.blocks.subtableInput.noVisibleFields')}
            description={i18n('card.blocks.subtableInput.noVisibleFieldsMessage')}
            buttonTitle={i18n('card.blocks.subtableInput.configureSubtable')}
            onButtonPress={onConfigure}
            imageSize={grid(12)}
            imageTintColor={Colors.v2.green30}
          />
        </FormScrollView>
      </View>
    </View>
  );
}

function EditRowForm({
  workflowTemplate,
  isAuthenticated,
  row,
  isReadOnly,
  onSaved,
  onClose,
}: RowFormProps & { row: SubtableRow; isReadOnly: boolean }) {
  const viewTemplateId = workflowTemplate.viewTemplateDefaults?.update;
  const hasViewTemplate = !isNil(viewTemplateId);
  const {
    fields,
    updateField,
    components,
    errors,
    saveAttempted,
    viewTemplate,
    status,
    isSaving,
    isDirty,
    onPressSave,
  } = useEditCardEntity({
    entityId: row.entityId,
    workflowTemplateId: workflowTemplate.id,
    viewTemplateId,
    isReadOnly,
  });
  const deleteEntities = useDeleteEntitiesMutation(workflowTemplate.id);
  const { deleteRow, isDeleting } = useDeleteRow({
    remove: () => deleteEntities([row.entityId]),
    onDeleted: () => {
      onSaved();
      onClose();
    },
  });
  // Leaving the row with unsaved edits asks first: opening another row or a related card, closing it,
  // or leaving the card. Not once it's saved or deleted, which close it on purpose. Before
  // useCloseOnDone, so a saved form's close isn't warned about.
  useUnsavedChangesNavigationWarning(isDirty && status !== 'success' && !isDeleting);
  useCloseOnDone(status === 'success', onSaved, onClose);

  return (
    <RowFormLayout
      isAuthenticated={isAuthenticated}
      formType="EDIT"
      title={getRowTitle(row)}
      workflowTemplateId={workflowTemplate.id}
      viewTemplateId={viewTemplate?.id}
      isLoading={isNil(fields) || (hasViewTemplate && isNil(viewTemplate))}
      isSaving={isSaving}
      saveLabel={i18n('common.save')}
      // Only once the row has changed.
      showSave={!isReadOnly && isDirty}
      isDirty={isDirty}
      isReadOnly={isReadOnly}
      fields={fields}
      updateField={updateField}
      components={components}
      errors={errors}
      saveAttempted={saveAttempted}
      onSave={onPressSave}
      onDelete={isReadOnly ? undefined : deleteRow}
      isDeleting={isDeleting}
      onClose={onClose}
    />
  );
}

/**
 * A draft row on the Create form: its own Create form, seeded with what was added, whose Save
 * writes back to the draft. Required fields are checked on Save, as when the row was added.
 */
function DraftRowForm({
  workflowTemplate,
  isAuthenticated,
  row,
  drafts,
  onSaved,
  onClose,
}: RowFormProps & { row: SubtableRow; drafts: SubtableFieldDrafts }) {
  const draft = drafts.rows.find(draftRow => draftRow.id === row.entityId);
  // Already created under the card (a retry after some rows failed): shown, but no longer a draft
  // to change.
  const isSaved = !isNil(draft?.savedEntityId);
  // Seeded once: saving updates the draft, which mustn't reseed the form under the user.
  const [initialFields] = useState(() => draft?.fields);
  const viewTemplateId = draft?.viewTemplateId ?? workflowTemplate.viewTemplateDefaults?.create;
  const hasViewTemplate = !isNil(viewTemplateId);
  const {
    fields,
    updateField,
    components,
    errors,
    saveAttempted,
    viewTemplate,
    status,
    isDirty,
    onPressSave,
  } = useCreateCardEntity({
    workflowTemplateId: workflowTemplate.id,
    viewTemplateId,
    initialFields,
    onSaveDraft: updatedFields => drafts.update(row.entityId, updatedFields),
  });
  const { deleteRow, isDeleting } = useDeleteRow({
    remove: () => drafts.remove(row.entityId),
    onDeleted: () => {
      onSaved();
      onClose();
    },
  });
  const isDone = status === 'success';
  // The Create form's unsaved changes warning covers the open form (see NewRowForm).
  useReportDirtyRowForm(drafts, isDirty && !isDone && !isDeleting);
  useCloseOnDone(isDone, onSaved, onClose);

  return (
    <RowFormLayout
      isAuthenticated={isAuthenticated}
      formType="CREATE"
      title={getRowTitle(row)}
      workflowTemplateId={workflowTemplate.id}
      viewTemplateId={viewTemplate?.id}
      isLoading={isNil(fields) || (hasViewTemplate && isNil(viewTemplate))}
      isSaving={false}
      saveLabel={i18n('common.save')}
      // Only once the row has changed.
      showSave={isDirty && !isSaved}
      isDirty={isDirty}
      isReadOnly={isSaved}
      fields={fields}
      updateField={updateField}
      components={components}
      errors={errors}
      saveAttempted={saveAttempted}
      onSave={onPressSave}
      onDelete={isSaved ? undefined : deleteRow}
      isDeleting={isDeleting}
      onClose={onClose}
    />
  );
}

/** "Row 3", or "Row" for a row opened from the URL that isn't among the loaded rows. */
function getRowTitle(row: SubtableRow): string {
  if (isNil(row.rowNumber)) {
    return i18n('card.blocks.subtableInput.row');
  }
  return i18n('card.blocks.subtableInput.rowTitle', { number: row.rowNumber });
}

/**
 * Once the form is done (saved, or at the row limit): tell the field to reload its rows, then
 * close. From an effect, so the close navigation runs after the render that turned the form's
 * unsaved changes warning off.
 */
function useCloseOnDone(isDone: boolean, onSaved: () => void, onClose: () => void) {
  // Once: the refetch re-renders the field, which hands down new callbacks before the menu closes.
  const hasClosedRef = useRef(false);
  useEffect(() => {
    if (isDone && !hasClosedRef.current) {
      hasClosedRef.current = true;
      onSaved();
      onClose();
    }
  }, [isDone, onSaved, onClose]);
}

/**
 * Confirms, removes the row (deletes it, or drops a draft), and toasts; onDeleted then refetches
 * the rows and closes the menu.
 */
function useDeleteRow({
  remove,
  onDeleted,
}: {
  remove: () => Promise<unknown> | void;
  onDeleted: () => void;
}) {
  const confirm = useConfirm();
  const toast = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteRow = async () => {
    const confirmed = await confirm({
      title: i18n('card.blocks.subtableInput.deleteRowConfirmTitle'),
      message: i18n('card.blocks.subtableInput.deleteRowConfirmMessage'),
      confirmLabel: i18n('common.delete'),
      danger: true,
    });
    if (!confirmed) {
      return;
    }
    setIsDeleting(true);
    try {
      await remove();
      toast({ message: i18n('card.blocks.subtableInput.rowDeleted') });
      onDeleted();
    } catch (error) {
      console.error(error);
      setIsDeleting(false);
    }
  };

  return { deleteRow, isDeleting };
}

function RowFormLayout({
  isAuthenticated,
  formType,
  title,
  workflowTemplateId,
  viewTemplateId,
  isLoading,
  isSaving,
  saveLabel,
  showSave,
  isDirty,
  isReadOnly,
  fields,
  updateField,
  components,
  errors,
  saveAttempted,
  onSave,
  onDelete,
  isDeleting = false,
  onClose,
}: {
  formType: 'CREATE' | 'EDIT';
  title: string;
  workflowTemplateId: string;
  viewTemplateId?: string;
  isLoading: boolean;
  isSaving: boolean;
  saveLabel: string;
  showSave: boolean;
  /** Unsaved changes: the menu ignores Escape (see closeOnEscape). */
  isDirty: boolean;
  isReadOnly: boolean;
  isAuthenticated: boolean;
  fields: FieldRecord | null;
  updateField: UpdateField;
  components: API.AnyComponent[];
  errors: CardEntityError[];
  saveAttempted: boolean;
  onSave: () => void;
  /** Shows a delete button below the form (an existing row on an editable card). */
  onDelete?: () => void;
  isDeleting?: boolean;
  /**
   * The close button's. Not the menu's own close, which would unmount the form, and its unsaved
   * changes warning, before the close navigation could ask.
   */
  onClose: () => void;
}) {
  // A read-only card shows its rows read-only too, the same way
  // applyComponentOverrides marks the card's own fields.
  const formComponents = useMemo(() => {
    if (isReadOnly) {
      return components.map(component => ({ ...component, readonly: true }));
    } else {
      return components;
    }
  }, [components, isReadOnly]);

  // AnchoredView focuses the first text input when the panel opens, but the fields arrive after
  // that, so focus the first focusable element once they've rendered. The focus trap leaves focus
  // that's already inside the panel alone, so this sticks.
  const [formElement, setFormElement] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isLoading) {
      formElement?.querySelector<HTMLElement>(FIRST_FOCUSABLE_SELECTOR)?.focus();
    }
  }, [isLoading, formElement]);

  // The card's save hotkey, scoped to this form: registered on the form element, it only hears
  // keys pressed inside the menu, and the match stops propagation, so the card form's
  // document-level Save never sees them. Registered even while the button is hidden, so Mod+S on
  // an unchanged row does nothing rather than saving the card.
  const saveHotkey = useHotkeyBinding('saveCard');
  const [showHotkeyTooltips] = useShowHotkeyTooltips();
  const canSave = showSave && !isLoading && !isSaving;
  const onSaveHotkey = () => {
    if (canSave) {
      onSave();
    }
  };

  return (
    <View ref={setFormElement} className={styles.form} data-dirty={isDirty || undefined}>
      <HotKey
        hotkey={saveHotkey}
        callback={onSaveHotkey}
        enabled={!isNil(formElement)}
        options={{ target: formElement }}
      />
      <Menu.TitleBar
        title={title}
        startContent={<Menu.CloseButton onClick={onClose} />}
        endContent={
          showSave && (
            <Button
              variant="primary"
              size="medium"
              label={saveLabel}
              loading={isSaving}
              disabled={isLoading}
              onClick={onSave}
              tooltip={saveLabel}
              tooltipHotkey={saveHotkey}
              tooltipPosition="bottom"
              tooltipDisabled={isSaving || !showHotkeyTooltips}
            />
          )
        }
      />
      {isLoading && (
        <View className={styles.loading}>
          <LoadingIndicator />
        </View>
      )}
      {!isLoading && !isNil(fields) && (
        <FormScrollProvider>
          <FormSearchProvider>
            <FormScrollView className={styles.body}>
              <View className={styles.formContent}>
                <CardFormTypeContext.Provider value={formType}>
                  <SubformNavigationContext
                    fields={fields}
                    updateField={updateField}
                    saveAttempted={saveAttempted}
                    apiErrors={errors}
                    isAuthenticated={isAuthenticated}
                  >
                    {/* The card's subtable routes are its own subtables'; one inside this row
                        opens from local state. */}
                    <SubtableRouteContext.Provider value={undefined}>
                      {/* Nor are its drafts: a subtable inside a row that doesn't exist yet
                          stays hidden. */}
                      <SubtableDraftsContext.Provider value={undefined}>
                        <EntityForm
                          fields={fields}
                          updateField={updateField}
                          components={formComponents}
                          errors={errors}
                          workflowTemplateId={workflowTemplateId}
                          viewTemplateId={viewTemplateId}
                          isAuthenticated={isAuthenticated}
                        />
                      </SubtableDraftsContext.Provider>
                    </SubtableRouteContext.Provider>
                  </SubformNavigationContext>
                </CardFormTypeContext.Provider>
              </View>
              {!isNil(onDelete) && (
                <View center padding={1}>
                  <Button
                    variant="secondary"
                    icon="trash"
                    danger
                    label={i18n('card.blocks.subtableInput.deleteRow')}
                    loading={isDeleting}
                    // Don't pass the promise to the button, so it remains enabled
                    // and can receive focus back when the confirm closes.
                    onClick={() => {
                      void onDelete();
                    }}
                  />
                </View>
              )}
            </FormScrollView>
          </FormSearchProvider>
        </FormScrollProvider>
      )}
    </View>
  );
}
